import { useQuery, UseQueryOptions } from 'react-query'
import { reasoningTracesApi } from './api/reasoning-traces'
import {
  ReasoningTrace,
  ReasoningTracesListResponse,
  ReasoningTraceFilters,
  ConversationsListResponse,
} from './types'
import { mockReasoningTraces } from '@/mocks/reasoning-traces/mockData'
import storage from '@/utils/storage'

// Toggle between mock and real API
// Set to false to use real backend API on localhost:8765
const USE_MOCK = false

const getMockTraces = (params: {
  page?: number
  limit?: number
  filters?: ReasoningTraceFilters
}): ReasoningTracesListResponse => {
  const { page = 1, limit = 10, filters = {} } = params

  // Apply filters
  let filteredTraces = [...mockReasoningTraces]

  if (filters.status) {
    filteredTraces = filteredTraces.filter(
      (trace) => trace.status === filters.status,
    )
  }

  if (filters.search) {
    const searchLower = filters.search.toLowerCase()
    filteredTraces = filteredTraces.filter(
      (trace) =>
        trace.title.toLowerCase().includes(searchLower) ||
        trace.session_id.toLowerCase().includes(searchLower),
    )
  }

  if (filters.startDate) {
    filteredTraces = filteredTraces.filter(
      (trace) => new Date(trace.started_at) >= new Date(filters.startDate!),
    )
  }

  if (filters.endDate) {
    filteredTraces = filteredTraces.filter(
      (trace) => new Date(trace.started_at) <= new Date(filters.endDate!),
    )
  }

  // Apply pagination
  const startIndex = (page - 1) * limit
  const endIndex = startIndex + limit
  const paginatedTraces = filteredTraces.slice(startIndex, endIndex)

  return {
    traces: paginatedTraces,
    total: filteredTraces.length,
    page,
    limit,
    hasMore: endIndex < filteredTraces.length,
  }
}

const getMockTraceById = (sessionId: string): ReasoningTrace | null => {
  return (
    mockReasoningTraces.find((trace) => trace.session_id === sessionId) || null
  )
}

// React Query hooks
export const useConversations = (
  options?: UseQueryOptions<ConversationsListResponse>,
) => {
  return useQuery<ConversationsListResponse>(
    ['reasoning-conversations'],
    async () => {
      if (USE_MOCK) {
        // Mock conversations for testing
        await new Promise((resolve) => setTimeout(resolve, 300))
        return {
          conversations: [
            {
              conversation_id: 'dm_user123',
              name: 'Direct Messages',
              type: 'DM' as const,
              participants: ['user123'],
              last_activity: new Date().toISOString(),
              trace_count: mockReasoningTraces.length,
            },
          ],
          total: 1,
        }
      }
      return reasoningTracesApi.getConversations()
    },
    options,
  )
}

export const useReasoningTraces = (
  params: {
    page?: number
    limit?: number
    filters?: ReasoningTraceFilters
  },
  options?: UseQueryOptions<ReasoningTracesListResponse>,
) => {
  return useQuery<ReasoningTracesListResponse>(
    ['reasoning-traces', params],
    async () => {
      if (USE_MOCK) {
        // Simulate network delay
        await new Promise((resolve) => setTimeout(resolve, 500))
        return getMockTraces(params)
      }
      return reasoningTracesApi.getTraces(params)
    },
    {
      keepPreviousData: true,
      ...options,
    },
  )
}

export const useReasoningTrace = (
  sessionId: string,
  options?: UseQueryOptions<ReasoningTrace>,
) => {
  return useQuery<ReasoningTrace>(
    ['reasoning-trace', sessionId],
    async () => {
      if (USE_MOCK) {
        // Simulate network delay
        await new Promise((resolve) => setTimeout(resolve, 300))
        const trace = getMockTraceById(sessionId)
        if (!trace) {
          throw new Error('Reasoning trace not found')
        }
        return trace
      }
      return reasoningTracesApi.getTraceById(sessionId)
    },
    {
      enabled: !!sessionId,
      ...options,
    },
  )
}

// Utility functions
export const downloadTraceFile = async (
  sessionId: string,
  fileIdOrUrl: string,
  filename: string,
) => {
  try {
    if (USE_MOCK) {
      const blob = new Blob(['Mock file content'], { type: 'text/plain' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } else {
      // For real API, if fileIdOrUrl is a full URL (from backend download_url), use it directly
      // Otherwise construct the download URL
      let downloadUrl: string

      if (fileIdOrUrl.startsWith('http')) {
        // It's already a full URL from the backend
        downloadUrl = fileIdOrUrl
      } else {
        // Construct the URL - this shouldn't happen with our new API structure
        const symphonyId =
          storage.getSymphonyUserId() || storage.getUserId() || 'test_user_001'
        downloadUrl = `http://localhost:8765/api/reasoning/file/${symphonyId}/${sessionId}/${fileIdOrUrl}`
      }

      // Use direct download via anchor tag to avoid CORS issues
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = filename
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  } catch (error) {
    console.error('Error downloading file:', error)
    throw error
  }
}

export const getFilePreviewUrl = (
  sessionId: string,
  fileId: string,
): string => {
  if (USE_MOCK) {
    return `/mock-preview/${sessionId}/${fileId}`
  }
  return reasoningTracesApi.getFilePreviewUrl(sessionId, fileId)
}

// Hook for fetching reports for a reasoning trace
export const useReasoningTraceReports = (
  sessionId: string,
  options?: UseQueryOptions<any>,
) => {
  return useQuery(
    ['reasoning-trace-reports', sessionId],
    async () => {
      if (USE_MOCK) {
        // Return mock reports structure
        return {
          success: true,
          reports: [],
          summary: {
            total_files: 0,
            total_size: 0,
            categories: {},
            has_dqr_report: false,
            has_transcripts: false,
          },
        }
      }
      return reasoningTracesApi.getReports(sessionId)
    },
    {
      enabled: !!sessionId,
      ...options,
    },
  )
}

// Utility function to get report viewer URL
export const getReportViewerUrl = (
  sessionId: string,
  reportPath: string,
): string => {
  if (USE_MOCK) {
    return `/mock-report-viewer/${sessionId}/${reportPath}`
  }
  return reasoningTracesApi.getReportViewerUrl(sessionId, reportPath)
}
